/**
 * Configurações centralizadas da API
 *
 * Todas as timeouts e configurações relacionadas à API devem ser definidas aqui
 * para garantir consistência entre web e apps nativos.
 */

export const API_CONFIG = {
  /**
   * Timeout padrão para requisições (web)
   * 20 segundos é um bom balanço entre UX e tempo de resposta
   */
  DEFAULT_TIMEOUT: 20000,

  /**
   * Timeout para apps nativos (iOS/Android)
   * Apps nativos podem ter timeouts mais longos devido à latência de rede móvel
   */
  NATIVE_TIMEOUT: 30000,

  /**
   * Timeout para requisições de upload/download de arquivos
   */
  FILE_TIMEOUT: 60000,

  /**
   * Timeout para requisições de relatórios/pdfs (podem demorar mais)
   */
  REPORT_TIMEOUT: 90000,

  /**
   * Número máximo de retries para requisições falhas
   */
  MAX_RETRIES: 2,

  /**
   * Delay entre retries (exponential backoff base)
   */
  RETRY_DELAY: 1000,

  /**
   * Configurações de cache por endpoint (em milissegundos)
   */
  CACHE_TTL: {
    /** Perfil e dados do usuário (5 minutos) */
    USER_PROFILE: 5 * 60 * 1000,

    /** Lista de veículos (2 minutos) */
    VEHICLES_LIST: 2 * 60 * 1000,

    /** Dashboard (1 minuto) */
    DASHBOARD: 60 * 1000,

    /** Dados estáticos como tipos, categorias, etc (30 minutos) */
    STATIC_DATA: 30 * 60 * 1000,

    /** Agendamentos (3 minutos) */
    AGENDAMENTOS: 3 * 60 * 1000,

    /** Histórico (5 minutos) */
    HISTORY: 5 * 60 * 1000,

    /** Padrão para requisições sem TTL específica (2 minutos) */
    DEFAULT: 2 * 60 * 1000,
  } as const,

  /**
   * Endpoints que devem usar cache-first strategy
   */
  CACHE_FIRST_ENDPOINTS: [
    '/user/profile',
    '/vehicles',
    '/dashboard',
    '/agendamentos/tipos',
    '/static',
  ],

  /**
   * Endpoints que nunca devem ser cacheados
   */
  NO_CACHE_ENDPOINTS: [
    '/auth/login',
    '/auth/logout',
    '/auth/refresh',
    '/sanctum/csrf-cookie',
    '/qr-code',
  ],

  /**
   * Determina o TTL de cache para um endpoint específico
   */
  getCacheTTL(endpoint: string): number {
    for (const [key, ttl] of Object.entries(this.CACHE_TTL)) {
      if (key === 'DEFAULT') continue
      if (endpoint.toLowerCase().includes(key.toLowerCase().replace('_', '/'))) {
        return ttl
      }
    }
    return this.CACHE_TTL.DEFAULT
  },

  /**
   * Verifica se um endpoint deve usar cache
   */
  shouldCache(endpoint: string): boolean {
    return !this.NO_CACHE_ENDPOINTS.some((noCache) => endpoint.includes(noCache))
  },

  /**
   * Verifica se um endpoint deve usar cache-first strategy
   */
  shouldUseCacheFirst(endpoint: string): boolean {
    return this.CACHE_FIRST_ENDPOINTS.some((cacheFirst) =>
      endpoint.includes(cacheFirst),
    )
  },

  /**
   * Obtém o timeout apropriado baseado no tipo de requisição
   */
  getTimeout(endpoint?: string, isNative = false): number {
    if (!endpoint) {
      return isNative ? this.NATIVE_TIMEOUT : this.DEFAULT_TIMEOUT
    }

    // Verificar se é uma requisição de arquivo/relatório
    if (endpoint.includes('/export') || endpoint.includes('/pdf') || endpoint.includes('/report')) {
      return this.REPORT_TIMEOUT
    }

    if (endpoint.includes('/upload') || endpoint.includes('/download')) {
      return this.FILE_TIMEOUT
    }

    return isNative ? this.NATIVE_TIMEOUT : this.DEFAULT_TIMEOUT
  },
} as const

/**
 * Tipo para os TTLS de cache
 */
export type CacheTTLKey = keyof typeof API_CONFIG.CACHE_TTL
